<template>
	<div class="branches-page">
		<!-- definePageMeta -->
		<Head>
			<Title>{{ metaContent?.title ? metaContent?.title : `${locale === 'pl' ? cmsLocation?.namePL : cmsLocation?.nameEN} | CarFree` }}</Title>
			<Meta name="description" :content="metaContent?.description ?? ''" />
			<Meta v-if="config.public.environment === 'production-to-change' && robotsCalculated" name="robots" :content="robotsCalculated ?? ''" />
			<Meta v-else name="robots" content="noindex, nofollow" />
			<Link rel="canonical" :href="metaContent?.canonical && metaContent?.canonical?.trim() !== '' ? metaContent?.canonical : url.href" />

			<!-- OG tags -->
			<Meta
				property="og:title"
				:content="metaContent?.title ? metaContent?.title : `${locale === 'pl' ? cmsLocation?.namePL : cmsLocation?.nameEN} | CarFree`" />
			<Meta property="og:description" :content="metaContent?.description ?? ''" />
			<Meta
				property="og:image"
				:content="structuralDataIMG ? `${storageUrl}${structuralDataIMG}` : bannerIMG ? `${storageUrl}${bannerIMG}` : ''" />
			<Meta property="og:url" :content="metaContent?.canonical && metaContent?.canonical?.trim() !== '' ? metaContent?.canonical : url.href" />
			<Meta property="og:type" content="website" />
			<Meta property="og:site_name" content="CarFree" />

			<Meta name="twitter:card" content="summary_large_image" />
			<Meta
				name="twitter:title"
				:content="metaContent?.title ? metaContent?.title : `${locale === 'pl' ? cmsLocation?.namePL : cmsLocation?.nameEN} | CarFree`" />
			<Meta name="twitter:description" :content="metaContent?.description ?? ''" />
			<Meta
				name="twitter:image"
				:content="structuralDataIMG ? `${storageUrl}${structuralDataIMG}` : bannerIMG ? `${storageUrl}${bannerIMG}` : ''" />
		</Head>

		<GlobalHeader />
		<CarRentalPageHero v-if="isElectricCarsPage" :title="$t('pages.electricCars.header')" />
		<LocationPageHero v-else />
		<div class="branches-page__cars-carousell">
			<HomePageChooseCar
				v-if="cmsLocation"
				:gray-bg="true"
				noTitle
				whiteCard
				:oneGroup="isElectricCarsPage ? EGroupName.ELECTRIC : carGroupSwitch"
				init />
		</div>
		<template v-if="isElectricCarsPage">
			<LazyVisibleLongTermPageBenefits hydrate-on-visible />
			<LazyVisibleLongTermPageContact
				:min-val="1"
				:max-val="30"
				:label="EFormLabel.DAYS"
				hydrate-on-visible
				:timeframe="EContactFormRentalTimeframe.SHORT_TERM" />
		</template>
		<template v-else>
			<LazyVisibleLocationMapSection hydrate-on-visible />
			<LazyVisibleLocationTestimonials hydrate-on-visible />
		</template>
		<LazyVisibleLocationTextSection hydrate-on-visible />
		<LazyVisibleLocationFaq hydrate-on-visible />
		<LazyVisibleLocationClosestBranches v-if="!isElectricCarsPage" hydrate-on-visible />
		<LazyVisibleLocationTextSection bottom hydrate-on-visible />
		<LazyVisibleGlobalApplicationBanner :without-margin="true" hydrate-on-visible />
		<LazyVisibleGlobalFooter hydrate-on-visible />
	</div>
</template>

<script setup lang="ts">
import { ELocationSectionType } from '@/modules/adminModule/constants/pagesCMSconstants'
const url = useRequestURL()
const { locale } = useI18n()
const { $notyf } = useNuxtApp()
const route = useRoute()
const locationRedirectStore = useLocationRedirectStore()
const i18nService = usei18nService()
const bookerStore = useBookerStore()
const bookerState = ref<IBookerState>(bookerStore.getBooker())
const reservationStore = useReservationStore()
// sections
const pageSections = ref<ICMSLocationContent[]>()
const pageHeaderSection = ref<ICMSLocationContent>()
const pageMapSection = ref<ICMSLocationContent>()
const pageTestimonialsSection = ref<ICMSLocationContent>()
const pageTextSection = ref<ICMSLocationContent>()
const pageFaqSection = ref<ICMSLocationContent>()
const pageTextSection2 = ref<ICMSLocationContent>()
const pageMetaObject = ref<ICMSLocationContent>()
const metaContent = ref<ICMSLocationContentTranslations>()
const structuralDataSection = ref<ICMSLocationContentTranslations>()
const config = useNuxtApp().$config
const cmsLocation = ref<ICMSLocationModel>()
const assignedLocations = ref<ILocationModel[]>([])
const closestLocations = ref<ICMSLocationModel[]>([])
const router = useRouter()
const localePath = useLocalePath()
const jsonLd = ref<any>()
const rentalLocationPagesStore = useRentalLocationPagesStore()
const carGroupSwitch = computed(() => {
	const cmsLocationLink = cmsLocation.value?.urlPL
	let group: EGroupName | undefined
	if (!cmsLocationLink) return undefined
	// const linkLocation = route.path

	// if (linkLocation.includes('elektrycznych')) {
	// 	return EGroupName.ELECTRIC
	// } else if (linkLocation.includes('busow-osobowych') || linkLocation.includes('-bus')) {
	// 	return EGroupName.VAN
	// } else return undefined

	if (cmsLocationLink.includes('elektrycznych') || cmsLocationLink.includes('tesla')) {
		group = EGroupName.ELECTRIC
	} else if (cmsLocationLink.includes('dostawczy')) {
		group = EGroupName.DELIVERY
	} else if (cmsLocationLink.includes('bus')) {
		group = EGroupName.VAN
	}

	console.log('group', group)

	return group
})

const { storageUrl } = useStorageService()

const isElectricCarsPage = computed(() => {
	return cmsLocation.value?.urlPL?.includes('elektrycznych') || cmsLocation.value?.urlEN?.includes('electric')
})

const fetchCMSLocationByURL = async () => {
	const locationURLSlug = route.params.urlSlug

	try {
		// const { data } = await ApiService.fetchCMSLocationBySlug(locationURLSlug, locale.value, true)
		// if (data) cmsLocation.value = data
		const data = await rentalLocationPagesStore.fetchCmsLocationBySlug(locationURLSlug as string, locale.value)
		if (data) cmsLocation.value = data
	} catch (error) {
		if ((error as IError).message?.includes('Location not found') || (error as IError).message?.includes('Lokalizacja nie znaleziona')) {
			router.push(localePath('/'))
		}
		console.log(error)
	}
}

const fetchAssignedLocations = async () => {
	if (!cmsLocation.value) return
	try {
		// const { data } = await ApiService.fetchAssignedLocations(cmsLocation.value.id)

		const data = await rentalLocationPagesStore.fetchAssignedLocations(cmsLocation.value.id)

		if (data && data.length > 0) {
			assignedLocations.value = data.map((location) => location.location)
		} else {
			assignedLocations.value = []
		}
	} catch (error) {
		console.log(error)
	}
}
const fetchClosestLocations = async () => {
	try {
		if (!cmsLocation.value) return
		// const { data } = await ApiService.fetchClosestLocations(cmsLocation.value.id)
		const data = await rentalLocationPagesStore.fetchClosestLocations(cmsLocation.value.id)

		if (data && data.length > 0) {
			closestLocations.value = data
		} else {
			closestLocations.value = []
		}
	} catch (error) {
		console.log(error)
	}
}

const getPageSections = async () => {
	if (!cmsLocation.value) return
	try {
		// const response = await ApiService.fetchCMSLocationContentSections(cmsLocation.value?.id, locale.value)
		const response = await rentalLocationPagesStore.fetchCMSLocationContentSections(cmsLocation.value?.id, locale.value)
		pageSections.value = response
		pageHeaderSection.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.PAGE_HEADER_IMAGE)
		pageMapSection.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.MAP_SECTION)
		pageTestimonialsSection.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.TESTIMONIALS)
		pageTextSection.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.CONTENT_SECTION_1)
		pageFaqSection.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.FAQ_SECTION)
		pageTextSection2.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.CONTENT_SECTION_2)
		pageMetaObject.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.META)
		metaContent.value = pageMetaObject.value?.translations.find((translation) => translation.language === locale.value)?.content[0]
		structuralDataSection.value = pageSections.value?.find((section) => section?.name === ELocationSectionType.STRUCTURAL_DATA)
	} catch (error) {
		$notyf.error('Error while fetching page sections')
		console.log(error)
	}
}

await fetchCMSLocationByURL()

await Promise.all([fetchAssignedLocations(), fetchClosestLocations(), getPageSections()])

const bannerIMG = ref<string>()

const structuralDataIMG = ref<string>()

const assignBannerIMG = () => {
	if (!pageHeaderSection.value) return null
	if (pageHeaderSection.value.files && pageHeaderSection.value.files.length > 0) {
		const img = pageHeaderSection.value.files[0].path
		if (img) bannerIMG.value = img
	}
}

assignBannerIMG()

const assignStructuralDataIMG = () => {
	if (!structuralDataSection.value) return null
	if (structuralDataSection.value.files && structuralDataSection.value.files.length > 0) {
		const img = structuralDataSection.value.files[0].path
		if (img) structuralDataIMG.value = img
	}
}

assignStructuralDataIMG()

const robotsCalculated = computed(() => {
	if (!metaContent.value?.robots || metaContent.value?.robots?.length === 0) return undefined
	return metaContent.value?.robots.join(', ')
})

watch(
	() => locationRedirectStore.getRedirectLang,
	(lang) => {
		if (!lang) return
		i18nService.changeLang(lang, cmsLocation.value[`url${lang.toUpperCase()}`])
	},
)

onMounted(() => {
	if (assignedLocations.value.length > 0) {
		bookerState.value.selectedLocations[EBookerLocationType.START] = assignedLocations.value[0]
		reservationStore.setBookerLocations({ ...bookerState.value.selectedLocations, end: null })
		reservationStore.addLocationToHistory(assignedLocations.value[0].id)
	}
})

const formatPhoneNumber = (phoneNumber: string) => {
	if (!phoneNumber || phoneNumber?.length < 9) return phoneNumber

	const phone = phoneNumber.replace('+', '')
	return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8, 11)}`.trim()
}

const phoneNumber = computed(() => {
	if (cmsLocation.value?.phoneNumber && cmsLocation.value?.phoneNumber !== '') {
		return formatPhoneNumber(cmsLocation.value?.phoneNumber)
	} else if (assignedLocations.value && assignedLocations.value?.length > 0) {
		return formatPhoneNumber(assignedLocations.value[0].phone_number)
	}
	return null
})

// STRUCTURAL DATA
const constructJsonld = () => {
	const structuralDataSectionContent = structuralDataSection.value?.translations.find((translation) => translation.language === locale.value)
		?.content[0]

	const structuralDataSectionContentPL = structuralDataSection.value?.translations.find((translation) => translation.language === 'pl')?.content[0]

	// main and name
	const isLocaleName =
		structuralDataSectionContent?.name && structuralDataSectionContent?.name?.trim() !== '' && structuralDataSectionContent?.name !== 'null'
	const isPLName =
		structuralDataSectionContentPL?.name && structuralDataSectionContentPL?.name?.trim() !== '' && structuralDataSectionContentPL?.name !== 'null'

	const isLocaleDesc =
		structuralDataSectionContent?.description &&
		structuralDataSectionContent?.description?.trim() !== '' &&
		structuralDataSectionContent?.description !== 'null'
	const isPLDesc =
		structuralDataSectionContentPL?.description &&
		structuralDataSectionContentPL?.description?.trim() !== '' &&
		structuralDataSectionContentPL?.description !== 'null'

	let tempJsonld = {
		'@context': 'https://schema.org',
		'@type': 'LocalBusiness',
		name: isLocaleName
			? structuralDataSectionContent.name
			: isPLName
				? structuralDataSectionContentPL.name
				: locale.value === 'pl'
					? cmsLocation.value?.namePL
					: cmsLocation.value?.nameEN,
		description: isLocaleDesc ? structuralDataSectionContent.description : isPLDesc ? structuralDataSectionContentPL.description : '',
		image: `${url.origin}/logo-cf.svg`,
		logo: `${url.origin}/logo-cf.svg`,
		url: url.href,
	}

	// image/logo
	// tempJsonld = {
	// 	...tempJsonld,
	// 	// image: `${storageUrl}${structuralDataIMG.value ? structuralDataIMG.value : bannerIMG.value}`,
	// }

	// address
	if (structuralDataSectionContentPL?.isAddress) {
		const isLocaleAddress =
			structuralDataSectionContent?.addressLocality &&
			structuralDataSectionContent?.addressLocality?.trim() !== '' &&
			structuralDataSectionContent?.addressLocality !== 'null' &&
			structuralDataSectionContent?.streetAddress &&
			structuralDataSectionContent?.streetAddress?.trim() !== '' &&
			structuralDataSectionContent?.streetAddress !== 'null' &&
			structuralDataSectionContent?.postalCode &&
			structuralDataSectionContent?.postalCode?.trim() !== '' &&
			structuralDataSectionContent?.postalCode !== 'null' &&
			structuralDataSectionContent?.addressRegion &&
			structuralDataSectionContent?.addressRegion?.trim() !== '' &&
			structuralDataSectionContent?.addressRegion !== 'null'

		let isPLAddress = false
		if (!isLocaleAddress) {
			isPLAddress =
				structuralDataSectionContentPL?.addressLocality &&
				structuralDataSectionContentPL?.addressLocality?.trim() !== '' &&
				structuralDataSectionContentPL?.addressLocality !== 'null' &&
				structuralDataSectionContentPL?.streetAddress &&
				structuralDataSectionContentPL?.streetAddress?.trim() !== '' &&
				structuralDataSectionContentPL?.streetAddress !== 'null' &&
				structuralDataSectionContentPL?.postalCode &&
				structuralDataSectionContentPL?.postalCode?.trim() !== '' &&
				structuralDataSectionContentPL?.postalCode !== 'null' &&
				structuralDataSectionContentPL?.addressRegion &&
				structuralDataSectionContentPL?.addressRegion?.trim() !== '' &&
				structuralDataSectionContentPL?.addressRegion !== 'null'
		}

		if (isLocaleAddress || isPLAddress) {
			tempJsonld = {
				...tempJsonld,
				address: {
					'@type': 'PostalAddress',
					streetAddress: isLocaleAddress ? structuralDataSectionContent.streetAddress : structuralDataSectionContentPL.streetAddress,
					addressLocality: isLocaleAddress ? structuralDataSectionContent.addressLocality : structuralDataSectionContentPL.addressLocality,
					addressRegion: isLocaleAddress ? structuralDataSectionContent.addressRegion : structuralDataSectionContentPL.addressRegion,
					postalCode: isLocaleAddress ? structuralDataSectionContent.postalCode : structuralDataSectionContentPL.postalCode,
					addressCountry: 'PL',
				},
			}
		}
	}

	// rest information
	const isLocaleTelephone =
		structuralDataSectionContent?.telephone &&
		structuralDataSectionContent?.telephone?.trim() !== '' &&
		structuralDataSectionContent?.telephone !== 'null'
	const isPLTelephone =
		structuralDataSectionContentPL?.telephone &&
		structuralDataSectionContentPL?.telephone?.trim() !== '' &&
		structuralDataSectionContentPL?.telephone !== 'null'

	if (cmsLocation.value) {
		tempJsonld = {
			...tempJsonld,
			legalName: 'CarFree',
			vatID: '521 369 56 26',
			telephone: isLocaleTelephone
				? structuralDataSectionContent?.telephone
				: isPLTelephone
					? structuralDataSectionContentPL?.telephone
					: phoneNumber.value
						? phoneNumber.value
						: '+48 794 500 550',
			priceRange: '$',
			paymentAccepted: 'Cash, Credit Card',
			openingHours: 'Mo,Tu,We,Th,Fr,Sa,Su 00:00-23:59',
			openingHoursSpecification: [
				{
					'@type': 'OpeningHoursSpecification',
					dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
					opens: '00:00',
					closes: '23:59',
				},
			],
		}
	}

	// TODO: Google Reviews
	// format:
	//
	// tempJsonld = {
	// 	...tempJsonld,
	// 	aggregateRating: {
	// 		'@type': 'AggregateRating',
	// 		ratingValue: '4.4',
	// 		reviewCount: '89',
	// 	},
	// }

	jsonLd.value = tempJsonld
	useJsonld(jsonLd.value)
}

constructJsonld()

definePageMeta({
	middleware: ['car-rental'],
})

provide('cmsLocation', cmsLocation)
provide('assignedLocations', assignedLocations)
provide('closestLocations', closestLocations)
provide('pageSections', pageSections)
provide('pageHeaderSection', pageHeaderSection)
provide('pageMapSection', pageMapSection)
provide('pageTestimonialsSection', pageTestimonialsSection)
provide('pageTextSection', pageTextSection)
provide('pageFaqSection', pageFaqSection)
provide('pageTextSection2', pageTextSection2)
</script>

<style scoped lang="scss">
@import './index.scss';
</style>
